import { cn } from "@/shared/lib/utils";

/**
 * Approved lockups from docs/WanderFitness_StyleGuide.pdf §Logo Usage.
 *
 *   - "light" — on white:  WANDER = black,  FITNESS + globe = orange
 *   - "dark"  — on black:  WANDER = white,  FITNESS + globe = orange
 *   - "orange" — on orange: WANDER + globe = white, FITNESS = black
 *
 * Never recolor outside these combinations. Never separate the globe from
 * the wordmark. Never scale below 40px tall.
 */
export type LogoVariant = "light" | "dark" | "orange";

interface LogoProps {
  readonly variant?: LogoVariant;
  readonly size?: "sm" | "md" | "lg";
  readonly className?: string;
  readonly withWordmark?: boolean;
  readonly title?: string;
}

interface LogoColors {
  readonly wander: string;
  readonly fitness: string;
  readonly globe: string;
}

const VARIANT_COLORS: Readonly<Record<LogoVariant, LogoColors>> = {
  light: { wander: "#111827", fitness: "#F97316", globe: "#F97316" },
  dark: { wander: "#FFFFFF", fitness: "#F97316", globe: "#F97316" },
  orange: { wander: "#FFFFFF", fitness: "#111827", globe: "#FFFFFF" },
};

const SIZE_CLASSES: Readonly<Record<NonNullable<LogoProps["size"]>, string>> = {
  sm: "h-10", // 40px — the style guide's minimum digital size.
  md: "h-14",
  lg: "h-20",
};

export function Logo({
  variant = "light",
  size = "md",
  className,
  withWordmark = true,
  title = "WanderFitness",
}: LogoProps) {
  const colors = VARIANT_COLORS[variant];
  return (
    <div
      data-testid="brand-logo"
      data-variant={variant}
      className={cn("inline-flex items-center gap-3", SIZE_CLASSES[size], className)}
      role="img"
      aria-label={title}
    >
      <GlobeMark color={colors.globe} />
      {withWordmark && (
        <div className="flex flex-col justify-center leading-[0.9]">
          <span
            data-testid="brand-logo-wander"
            className="font-display text-[1.05em] tracking-tight"
            style={{ color: colors.wander }}
          >
            WANDER
          </span>
          <span
            data-testid="brand-logo-fitness"
            className="font-display text-[1.05em] tracking-tight"
            style={{ color: colors.fitness }}
          >
            FITNESS
          </span>
        </div>
      )}
    </div>
  );
}

function GlobeMark({ color }: { readonly color: string }) {
  // Circle with EKG heartbeat "equator" and three meridians. The heartbeat
  // replaces the middle latitude line — that's the identity mark from the
  // style guide ("globe with an EKG heartbeat equator line").
  return (
    <svg
      className="h-full w-auto"
      viewBox="0 0 64 64"
      fill="none"
      stroke={color}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="28" />
      {/* Central meridian ellipse — the two sides of the ellipse double
          as the left/right meridians, so we don't need a separate vertical
          line down the middle. */}
      <ellipse cx="32" cy="32" rx="12" ry="28" />
      {/* Top and bottom latitudes */}
      <path d="M8 22 Q 32 16 56 22" />
      <path d="M8 42 Q 32 48 56 42" />
      {/* EKG heartbeat "equator" — the identity mark from the style guide */}
      <path d="M6 32 H 20 L 24 24 L 30 42 L 36 22 L 42 36 L 46 32 H 58" strokeWidth={3.5} />
    </svg>
  );
}
